import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client, { resolveImageUrl } from "@/lib/api";
import { useAuth, startLogin } from "@/lib/auth";
import { Shield, LogOut, MessageSquare, MessageCircle, Sparkles, BookOpen, Award, LogIn, Crown, UserCircle2, Flag, Pencil, Trophy } from "lucide-react";
import EditProfileDialog from "@/components/EditProfileDialog";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return "—"; }
}

function CATEGORY_NAME(slug) {
  const map = {
    "announcements": "Announcements",
    "patch-notes": "Patch Notes",
    "diplomacy": "Diplomacy & Treaties",
    "economy-trade": "Economy & Trade",
    "nation-recruitment": "Nation Recruitment",
    "general": "General Discussion",
    "help-qa": "Help & Q&A",
    "strategy": "Strategy",
    "lore-roleplay": "Lore & Roleplay",
    "suggestions": "Suggestions",
    "bug-reports": "Bug Reports",
  };
  return map[slug] || slug;
}

export default function Profile() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    client.get("/auth/me/stats").then(r => setStats(r.data)).catch(() => setStats({ posts: 0, replies: 0, promoted: 0, recent_posts: [] }));
  }, [user]);

  if (loading) {
    return (
      <div className="text-center py-32 font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow" data-testid="profile-loading">
        // verifying clearance...
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="profile-signin">
        <UserCircle2 className="w-12 h-12 text-[var(--t-blue-glow)] mx-auto mb-4 opacity-70" />
        <h1 className="font-display text-3xl text-[var(--t-cyan)] uppercase mb-3">No Active Session</h1>
        <p className="text-[var(--t-text-dim)] mb-6">Sign in with Discord to see your Citizen profile.</p>
        <button onClick={startLogin} className="btn-primary" data-testid="profile-signin-btn">
          <LogIn className="w-4 h-4" /> Sign in with Discord
        </button>
      </div>
    );
  }

  const role = user.is_admin ? "Wiki Editor" : "Citizen";
  const RoleIcon = user.is_admin ? Crown : UserCircle2;
  const roleAccent = user.is_admin ? "text-[#fbbf24]" : "text-[var(--t-cyan)]";
  const roleBorder = user.is_admin ? "border-[#fbbf24]/50" : "border-[var(--t-blue)]/50";
  const roleBg = user.is_admin ? "bg-[rgba(251,191,36,0.08)]" : "bg-[rgba(58,160,255,0.08)]";

  const displayName = user.display_name || user.name || user.username || "Citizen";
  const effectiveAvatar = resolveImageUrl(user.profile_picture || user.picture);
  const banner = resolveImageUrl(user.profile_banner);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14" data-testid="profile-page">
      {/* HERO */}
      <div className="panel panel-glow mb-8 relative overflow-hidden">
        {/* Banner (custom or default gradient) */}
        <div className="relative h-32 md:h-44 w-full overflow-hidden" data-testid="profile-banner">
          {banner ? (
            <img src={banner} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="profile-banner-img" />
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-br from-[#0a1428] via-[#0d1f3a] to-[#050912]" />
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#5865F2]/25 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[var(--t-blue)]/20 blur-3xl pointer-events-none" />
            </>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050912]/70 pointer-events-none" />
        </div>
        <div className="px-6 md:px-8 pb-6 md:pb-7 -mt-12 md:-mt-14 relative">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-5 md:gap-7">
            {effectiveAvatar ? (
              <img
                src={effectiveAvatar}
                alt=""
                className="w-24 h-24 md:w-28 md:h-28 rounded-full border-2 border-[var(--t-border-glow)] shadow-[0_0_22px_rgba(58,160,255,0.45)] flex-shrink-0 bg-[#050912] object-cover"
                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                data-testid="profile-avatar"
              />
            ) : (
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full border-2 border-[var(--t-border-glow)] bg-[#050912] flex items-center justify-center text-[var(--t-blue-glow)] font-display text-3xl flex-shrink-0">
                {(user.name || user.username || "C").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">// Citizen Dossier</span>
              <h1 className="font-display text-3xl md:text-4xl text-[var(--t-text)] uppercase leading-tight mt-1 mb-2 truncate" data-testid="profile-name">
                {displayName}
              </h1>
              <div className="flex flex-wrap items-center gap-2 md:gap-3 font-mono text-[10px] md:text-xs text-[var(--t-text-muted)] tracking-[0.18em] uppercase">
                {user.username && (
                  <span className="text-[#7289DA]" data-testid="profile-handle">@{user.username}{user.discriminator && user.discriminator !== "0" ? `#${user.discriminator}` : ""}</span>
                )}
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border ${roleBorder} ${roleBg} ${roleAccent}`} data-testid="profile-role">
                  <RoleIcon className="w-3 h-3" /> {role}
                </span>
                {user.nation && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-[var(--t-green)]/50 bg-[rgba(57,255,20,0.06)] text-[var(--t-green)]" data-testid="profile-nation">
                    <Flag className="w-3 h-3" /> {user.nation}
                  </span>
                )}
                <span>· Joined {formatDate(user.created_at)}</span>
              </div>
              {user.bio && (
                <p className="text-sm md:text-base text-[var(--t-text-dim)] mt-3 leading-relaxed max-w-2xl whitespace-pre-wrap" data-testid="profile-bio">{user.bio}</p>
              )}
            </div>
            <div className="flex flex-col gap-2 w-full sm:w-auto flex-shrink-0">
              <button onClick={() => setEditOpen(true)} className="btn-ghost !text-xs !py-2 !px-4" data-testid="profile-edit-btn">
                <Pencil className="w-3.5 h-3.5" /> Edit Profile
              </button>
              {user.is_admin && (
                <button onClick={() => navigate("/admin")} className="btn-primary !text-xs !py-2 !px-4" data-testid="profile-admin-btn">
                  <Shield className="w-3.5 h-3.5" /> Open Admin
                </button>
              )}
              <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost !text-xs !py-2 !px-4" data-testid="profile-logout-btn">
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8" data-testid="profile-stats">
        <StatTile icon={MessageSquare} label="Forum Posts" value={stats?.posts ?? "—"} testid="stat-posts" />
        <StatTile icon={MessageCircle} label="Replies" value={stats?.replies ?? "—"} testid="stat-replies" />
        <StatTile icon={Sparkles} label="In Codex" value={stats?.promoted ?? "—"} accent testid="stat-promoted" />
        <StatTile icon={Trophy} label="Arcade Best" value={stats?.arcade_best_score ?? "—"} highlight testid="stat-arcade" />
      </div>

      {/* RECENT ACTIVITY + QUICK LINKS */}
      <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 md:gap-8">
        <section data-testid="profile-recent">
          <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">// Recent Transmissions</span>
          <h2 className="font-display text-xl md:text-2xl text-[var(--t-cyan)] uppercase mt-2 mb-4">Your Latest Posts</h2>
          {!stats || stats.recent_posts.length === 0 ? (
            <div className="panel p-7 text-center">
              <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-text-muted)] uppercase mb-3">// no transmissions yet</div>
              <p className="text-sm text-[var(--t-text-dim)] mb-4">You haven&apos;t started any threads. Open a channel.</p>
              <Link to="/forum/new" className="btn-primary !text-xs" data-testid="profile-newpost-btn">
                <MessageSquare className="w-3.5 h-3.5" /> Start a Thread
              </Link>
            </div>
          ) : (
            <div className="space-y-2.5">
              {stats.recent_posts.map(p => (
                <Link
                  key={p.id}
                  to={`/forum/${p.id}`}
                  className="panel block p-4 hover:border-[var(--t-blue)] transition"
                  data-testid={`profile-post-${p.id}`}
                >
                  <div className="flex items-center gap-2 flex-wrap font-mono text-[10px] tracking-[0.2em] text-[var(--t-blue-glow)] uppercase mb-1">
                    <span>{CATEGORY_NAME(p.category)}</span>
                    {p.promoted_slug && (
                      <span className="inline-flex items-center gap-1 text-[var(--t-green)]">
                        <Sparkles className="w-3 h-3" /> in codex
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-sm md:text-base text-[var(--t-cyan)] uppercase leading-tight mb-1">{p.title}</h3>
                  <div className="flex items-center gap-3 font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.15em] uppercase">
                    <span>{formatDate(p.created_at)}</span>
                    <span>·</span>
                    <span>{p.reply_count || 0} {p.reply_count === 1 ? "reply" : "replies"}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <aside className="space-y-3" data-testid="profile-quicklinks">
          <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">// Quick Links</span>
          <h2 className="font-display text-xl md:text-2xl text-[var(--t-cyan)] uppercase mt-2 mb-4">Jump In</h2>
          <QuickLink to="/forum" icon={MessageSquare} label="Open Forum" sub="Strategy, lore, diplomacy" />
          <QuickLink to="/wiki" icon={BookOpen} label="Browse Codex" sub="Every page in the wiki" />
          <QuickLink to="/badges" icon={Award} label="Badges Gallery" sub="Hunt the collection" />
          <QuickLink to="/contribute" icon={Sparkles} label="Submit a Tip" sub="Help us improve a page" />
        </aside>
      </div>

      <EditProfileDialog open={editOpen} onOpenChange={setEditOpen} />
    </div>
  );
}

function StatTile({ icon: Icon, label, value, accent = false, highlight = false, testid }) {
  const tone = highlight
    ? { border: "border-[#fbbf24]/40", icon: "text-[#fbbf24]", value: "text-[#fbbf24]" }
    : accent
      ? { border: "border-[var(--t-green)]/40", icon: "text-[var(--t-green)]", value: "text-[var(--t-green)]" }
      : { border: "", icon: "text-[var(--t-blue-glow)]", value: "text-[var(--t-cyan)]" };
  return (
    <div className={`panel p-4 md:p-5 text-center ${tone.border}`} data-testid={testid}>
      <Icon className={`w-4 h-4 mx-auto mb-2 ${tone.icon}`} />
      <div className={`font-display text-2xl md:text-3xl ${tone.value} leading-none`} data-testid={`${testid}-value`}>{value}</div>
      <div className="font-mono text-[10px] tracking-[0.22em] text-[var(--t-text-muted)] uppercase mt-2">{label}</div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, sub }) {
  return (
    <Link
      to={to}
      className="panel p-4 flex items-center gap-3 hover:border-[var(--t-blue)] transition group"
    >
      <div className="w-9 h-9 bg-[rgba(58,160,255,0.1)] border border-[var(--t-border-glow)] flex items-center justify-center flex-shrink-0 group-hover:border-[var(--t-blue)]">
        <Icon className="w-4 h-4 text-[var(--t-blue-glow)] group-hover:text-[var(--t-cyan)]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display text-sm tracking-[0.12em] uppercase text-[var(--t-cyan)] leading-tight">{label}</div>
        <div className="font-mono text-[10px] tracking-[0.15em] text-[var(--t-text-muted)] uppercase mt-0.5 truncate">{sub}</div>
      </div>
    </Link>
  );
}
